import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  UserRound,
  ShoppingCart,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Search,
  MapPin,
  Trash2,
  Package,
  ShieldCheck,
  Truck,
  Bell,
  Store,
} from "lucide-react";
import {
  EPP_LOGO,
  secondaryNavItems,
  drawerPrimaryItems,
  catalogueFamilies,
  catalogueDrawerPromo,
} from "../../data/content";
import { SITE_SHELL_MAX, DEMO_BANNER_HEIGHT_PX } from "../../constants/layout";
import { useUserAuth } from "../../context/UserAuthContext";
import { useDemoModeOptional } from "../../context/DemoModeContext";
import { useStore } from "../../context/StoreContext";
import { useCart } from "../../context/CartContext";
import { useCategoryChildren } from "../../hooks/useCatalog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import Marquee from "react-fast-marquee";
import {
  orderAccountLineHt,
  orderAccountLineTtc,
  strikePublicHtFromCartItem,
} from "../../utils/resolvedPriceDisplay";

/** Messages bandeau réassurance (desktop + mobile marquee). */
const REASSURANCE_BAR_ITEMS = [
  { icon: Package, text: "Stock vérifié en temps réel" },
  { icon: Truck, text: "Retrait en 2h · 5 agences" },
  { icon: ShieldCheck, text: "+25 ans d'expertise pro" },
  { icon: ShoppingCart, text: "Commande & devis 24h/24" },
];

/** Panneau menu : affiche le niveau 2 (sous-familles) depuis l’API. */
function MenuPanelLevel2({
  familySlug,
  familyLabel,
  children: childCategories,
  onClose,
}) {
  const basePath = `/catalogue/${familySlug}`;
  const title = familyLabel || familySlug;
  return (
    <div className="max-w-5xl">
      <header className="mb-6 flex flex-col gap-4 border-b border-gray-200/80 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#190276]/50">
            Sous-catégories
          </p>
          <h2 className="mt-1.5 font-heading text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
            {title}
          </h2>
        </div>
        <Link
          to={basePath}
          onClick={onClose}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#190276] px-5 py-2.5 text-sm font-bold text-white shadow-md ring-1 ring-white/10 transition hover:brightness-105 active:scale-[0.98]"
        >
          Voir tout le rayon
          <ChevronRight className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        </Link>
      </header>
      {childCategories?.length > 0 ? (
        <>
          <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
            Parcourir
          </p>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {childCategories.map((c) => (
              <li key={c.id}>
                <Link
                  to={`${basePath}/${c.slug}`}
                  onClick={onClose}
                  className="group flex min-h-[46px] items-center justify-between gap-2 rounded-xl border border-gray-200/90 bg-white px-3.5 py-2.5 text-left text-sm font-medium leading-snug text-gray-800 shadow-sm ring-1 ring-gray-100/70 transition hover:border-[#190276]/30 hover:bg-[#190276]/[0.03] hover:text-[#190276] hover:shadow-md"
                >
                  <span className="min-w-0">{c.name}</span>
                  <ChevronRight
                    className="h-4 w-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-[#190276]"
                    strokeWidth={2}
                    aria-hidden
                  />
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 px-5 py-8 text-center">
          <p className="text-sm text-gray-500">
            Aucune sous-catégorie listée ici pour l’instant.
          </p>
          <Link
            to={basePath}
            onClick={onClose}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#190276] px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:brightness-105"
          >
            Ouvrir le rayon {title}
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      )}
    </div>
  );
}

function MegaMenuPanelContent({ panel, onClose, hideViewAll }) {
  return (
    <div className={hideViewAll ? "" : "max-w-4xl"}>
      {!hideViewAll && (
        <Link
          to={panel.viewAllPath}
          onClick={onClose}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#190276] hover:text-[#130160] hover:underline transition-colors duration-200 mb-6"
        >
          {panel.viewAllLabel}
          <ChevronRight className="w-4 h-4 shrink-0" />
        </Link>
      )}
      <div
        className={`grid gap-x-6 gap-y-6 sm:gap-x-10 sm:gap-y-8 ${hideViewAll ? "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-y-8 sm:gap-y-10"}`}
      >
        {panel.columns?.map((col) => (
          <div key={col.title} className="min-w-0">
            <h4
              className={`font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100 pb-2 ${hideViewAll ? "text-[10px] mb-2" : "text-[11px] mb-3"}`}
            >
              {col.title}
            </h4>
            <ul className={hideViewAll ? "space-y-1.5" : "space-y-2"}>
              {col.links?.map((link) => (
                <li key={`${link.path}-${link.label}`}>
                  <Link
                    to={link.path}
                    onClick={onClose}
                    className={`text-gray-700 hover:text-[#190276] transition-colors duration-150 block rounded hover:bg-[#190276]/5 ${hideViewAll ? "text-xs py-1 -mx-1 px-1" : "text-sm py-0.5 -mx-1 px-1"}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catalogHoveredKey, setCatalogHoveredKey] = useState(null);
  const [mobileDrawerView, setMobileDrawerView] = useState("main");
  const [mobilePanelKey, setMobilePanelKey] = useState(null);
  const [connexionOpen, setConnexionOpen] = useState(false);
  const [agencyPopupOpen, setAgencyPopupOpen] = useState(false);
  const [pendingAgency, setPendingAgency] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [logoHeaderHeight, setLogoHeaderHeight] = useState(0);
  const [drawerMainWidth, setDrawerMainWidth] = useState(320);
  const headerRef = useRef(null);
  const logoHeaderRef = useRef(null);
  const desktopLogoRef = useRef(null);
  const menuContainerRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated, api: userApi, user } = useUserAuth();
  const isApprovedPro = user?.is_pro && user?.pro_status === "approved";
  const {
    agencies,
    selectedId,
    setSelectedId,
    suggestedStore,
    setSuggestedStoreId,
  } = useStore();
  const pickupAgencies = useMemo(
    () =>
      agencies.filter(
        (a) =>
          !a.isShowroom &&
          a.locationType !== "showroom" &&
          a.supportsClickCollect !== false,
      ),
    [agencies],
  );
  const showroomAgencies = useMemo(
    () => agencies.filter((a) => a.isShowroom || a.locationType === "showroom"),
    [agencies],
  );
  const activePanelKey = catalogHoveredKey || mobilePanelKey;
  const { children: panelChildren } = useCategoryChildren(activePanelKey || "");

  const [notifCount, setNotifCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchTimerRef = useRef(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

  const {
    items: cartItems,
    count: cartCount,
    loading: cartLoading,
    updating: cartUpdating,
    removeItem: removeCartItem,
    refresh: refreshCart,
  } = useCart();
  const [cartOpen, setCartOpen] = useState(false);

  const demoCtx = useDemoModeOptional();
  const demoOn = !!demoCtx?.isDemoSession;

  useEffect(() => {
    if (!isAuthenticated || !userApi) return;
    let active = true;
    const fetchCount = async () => {
      try {
        const res = await userApi("/notifications/count");
        if (res.ok && active) {
          const data = await res.json();
          setNotifCount(data.unread || 0);
        }
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => { active = false; clearInterval(interval); };
  }, [isAuthenticated, userApi]);

  useEffect(() => {
    if (!cartOpen) return;
    const handleClick = (e) => {
      if (
        e.target.closest("[data-cart-toggle]") ||
        e.target.closest("[data-cart-dropdown]")
      )
        return;
      setCartOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [cartOpen]);

  const toggleCart = (e) => {
    e.preventDefault();
    if (!cartOpen && isAuthenticated) refreshCart();
    setCartOpen((v) => !v);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = (e.target.search?.value || searchQuery || "").trim();
    if (q) navigate(`/catalogue?search=${encodeURIComponent(q)}`);
    else navigate("/catalogue");
    setMenuOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setSearchFocused(false);
  };

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const storeParam = selectedId ? `&store_id=${selectedId}` : "";
        const res = await fetch(
          `${API_BASE}/api/products?search=${encodeURIComponent(searchQuery)}&limit=6${storeParam}`,
        );
        if (res.ok) {
          const data = await res.json();
          setSearchResults(
            Array.isArray(data) ? data : data.items || data.products || [],
          );
        }
      } catch {
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery, API_BASE, selectedId]);

  useEffect(() => {
    if (!menuOpen) {
      setMobileDrawerView("main");
      setMobilePanelKey(null);
      setCatalogHoveredKey(null);
    }
  }, [menuOpen]);

  // Drawer desktop : min 320px, aligné logo + petite marge
  useEffect(() => {
    if (!menuOpen) return;
    const id = setTimeout(() => {
      const container = menuContainerRef.current;
      const logo = desktopLogoRef.current;
      if (container && logo) {
        const cr = container.getBoundingClientRect();
        const lr = logo.getBoundingClientRect();
        const fromLogo = Math.round(lr.right - cr.left + 12);
        setDrawerMainWidth(Math.max(320, fromLogo));
      }
    }, 0);
    return () => clearTimeout(id);
  }, [menuOpen]);

  useEffect(() => {
    const measure = () => {
      const h = headerRef.current?.offsetHeight ?? 0;
      setHeaderHeight(h);
      setLogoHeaderHeight(
        logoHeaderRef.current?.offsetHeight ??
          headerRef.current?.offsetHeight ??
          0,
      );
      const shellTop = (demoOn ? DEMO_BANNER_HEIGHT_PX : 0) + h;
      document.documentElement.style.setProperty("--epp-shell-top", `${shellTop}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (headerRef.current) ro.observe(headerRef.current);
    if (logoHeaderRef.current) ro.observe(logoHeaderRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      document.documentElement.style.removeProperty("--epp-shell-top");
    };
  }, [menuOpen, demoOn]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const selectedAgency = selectedId
    ? agencies.find((a) => a.id === selectedId)
    : null;
  const agencyLabel = (a) => {
    if (!a) return "Agence";
    if (a.isShowroom) return `Showroom · ${a.city || ""}`.trim();
    if (a.city) return `Agence · ${a.city}`;
    return "Agence";
  };

  const selectAgency = (agency) => {
    if (isAuthenticated && selectedId && selectedId !== agency.id) {
      setPendingAgency(agency);
      return;
    }
    confirmSelectAgency(agency);
  };

  const confirmSelectAgency = (agency) => {
    setPendingAgency(null);
    setSelectedId(agency.id);
    setAgencyPopupOpen(false);
    if (isAuthenticated && userApi) {
      userApi("/users/me/favorite-store", {
        method: "PUT",
        body: { store_id: agency.id },
      }).catch(() => {});
    }
  };

  const openDrawerProducts = () => {
    setMenuOpen(true);
    setMobileDrawerView("categories");
    setCatalogHoveredKey(null);
  };

  return (
    <>
      <motion.header
        ref={headerRef}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed left-0 right-0 z-50 overflow-visible bg-white"
        style={{ top: demoOn ? DEMO_BANNER_HEIGHT_PX : 0 }}
      >
        <div
          className={`w-full ${SITE_SHELL_MAX} mx-auto transition-all duration-500 ease-out border-b ${
            scrolled
              ? "bg-white border-gray-200/60 shadow-[0_4px_30px_-4px_rgba(0,0,0,0.07)]"
              : "bg-white border-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.02)]"
          }`}
        >
          <div ref={logoHeaderRef}>
            {/* ========== NIVEAU A — Bandeau réassurance (mobile : marquee ; desktop : ligne fixe) ========== */}
            <div className="border-b border-white/10 bg-[#130160] text-white/80" data-testid="ecom-banner">
              <div className="flex min-h-[36px] items-center md:hidden">
                <Marquee
                  speed={24}
                  gradient
                  gradientColor="#130160"
                  gradientWidth={36}
                  pauseOnHover
                  className="py-1.5"
                >
                  {[...REASSURANCE_BAR_ITEMS, ...REASSURANCE_BAR_ITEMS].map(
                    (item, i) => (
                      <div
                        key={`reassurance-m-${i}`}
                        className="mx-5 flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[10px] font-medium tracking-[0.05em] sm:text-[11px]"
                      >
                        <item.icon
                          className="h-3 w-3 shrink-0 text-white/45 sm:h-3.5 sm:w-3.5"
                          strokeWidth={1.7}
                        />
                        <span>{item.text}</span>
                      </div>
                    ),
                  )}
                </Marquee>
              </div>
              <div className="hidden h-8 min-h-0 w-full items-center justify-between gap-3 px-4 text-[10px] font-medium uppercase tracking-[0.12em] md:flex md:px-6 xl:px-8">
                {REASSURANCE_BAR_ITEMS.map((item, i) => (
                  <div
                    key={`reassurance-d-${i}`}
                    className="flex shrink-0 items-center gap-2 whitespace-nowrap"
                  >
                    <item.icon
                      className="h-3 w-3 shrink-0 text-white/45"
                      strokeWidth={1.7}
                    />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ========== NIVEAU B — Desktop ========== */}
            <div className="w-full px-4 md:px-6 xl:px-8">
              <div className="hidden lg:flex flex-row items-center gap-2 overflow-visible py-0 md:gap-3">
                <button
                  type="button"
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center justify-center w-11 h-11 rounded-lg text-gray-700 hover:text-[#190276] hover:bg-gray-50 transition-colors duration-200 z-[60] shrink-0"
                  aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                  aria-expanded={menuOpen}
                  data-testid="mobile-menu-toggle"
                >
                  {menuOpen ? (
                    <X className="w-7 h-7 stroke-[2]" />
                  ) : (
                    <Menu className="w-7 h-7 stroke-[2]" />
                  )}
                </button>
                <Link
                  ref={desktopLogoRef}
                  to="/"
                  className="flex shrink-0 items-center z-10 mr-5 lg:mr-7"
                  data-testid="logo-link"
                >
                  <img
                    src={EPP_LOGO}
                    alt="EPP - Euro Plomberie Piscines"
                    className="h-16 sm:h-20 md:h-24 lg:h-28 w-auto max-w-full object-contain"
                  />
                </Link>
                <form
                  onSubmit={handleSearchSubmit}
                  className="flex-1 min-w-0 mx-2 md:mx-4 relative"
                  role="search"
                  aria-label="Rechercher"
                >
                  <div className="flex items-center w-full h-11 border border-gray-200/80 rounded-xl overflow-hidden bg-gray-50/50 hover:bg-white hover:border-gray-300/80 focus-within:bg-white focus-within:border-[#190276]/40 focus-within:ring-2 focus-within:ring-[#190276]/10 focus-within:shadow-[0_0_0_4px_rgba(25,2,118,0.04)] transition-all duration-300">
                    <input
                      type="search"
                      name="search"
                      placeholder="Rechercher un produit, une marque, une référence..."
                      className="flex-1 min-w-0 h-full px-4 text-sm text-gray-800 placeholder:text-gray-400/80 focus:outline-none bg-transparent"
                      aria-label="Recherche catalogue"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() =>
                        setTimeout(() => setSearchFocused(false), 200)
                      }
                      autoComplete="off"
                    />
                    {searching && (
                      <span className="w-4 h-4 border-2 border-[#190276] border-t-transparent rounded-full animate-spin mr-3 shrink-0" />
                    )}
                    <button
                      type="submit"
                      className="flex items-center justify-center w-12 h-full bg-[#190276] text-white hover:bg-[#130160] active:scale-95 transition-all duration-200 shrink-0"
                      aria-label="Rechercher"
                    >
                      <Search className="w-5 h-5" />
                    </button>
                  </div>
                  {searchFocused && searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-2xl z-50 overflow-hidden max-h-[420px] overflow-y-auto">
                      {searchResults.map((p) => {
                        const img =
                          (p.images || []).find(
                            (u) =>
                              typeof u === "string" && u.startsWith("http"),
                          ) || p.image;
                        const price = p.price != null ? Number(p.price) : null;
                        const pStock = p.stock != null ? p.stock : null;
                        return (
                          <Link
                            key={p.id || p._id}
                            to={`/produit/${p.slug}`}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                            onClick={() => {
                              setSearchQuery("");
                              setSearchResults([]);
                              setSearchFocused(false);
                            }}
                          >
                            <div className="w-12 h-12 rounded-lg bg-gray-50 border border-gray-100 shrink-0 overflow-hidden flex items-center justify-center">
                              {img ? (
                                <img
                                  src={img}
                                  alt=""
                                  className="w-full h-full object-contain p-1"
                                  loading="lazy"
                                />
                              ) : (
                                <Search className="w-4 h-4 text-gray-300" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              {p.brand && (
                                <p className="text-[10px] font-bold text-gray-400 uppercase truncate">
                                  {p.brand}
                                </p>
                              )}
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {p.name}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {price != null && (
                                  <span className="text-sm font-bold text-[#190276]">
                                    {price.toFixed(2)} € TTC
                                  </span>
                                )}
                                {pStock != null && (
                                  <span
                                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${pStock > 0 ? "text-emerald-700 bg-emerald-50" : "text-red-600 bg-red-50"}`}
                                  >
                                    {pStock > 0 ? "En stock" : "Rupture"}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                          </Link>
                        );
                      })}
                      <Link
                        to={`/catalogue?search=${encodeURIComponent(searchQuery)}`}
                        className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-[#190276] hover:bg-[#190276]/5 transition-colors"
                        onClick={() => {
                          setSearchQuery("");
                          setSearchResults([]);
                          setSearchFocused(false);
                        }}
                      >
                        Voir tous les résultats{" "}
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  )}
                </form>
                <div className="flex items-center gap-3 ml-auto shrink-0">
                  {/* Bouton neutre — agence */}
                  <button
                    type="button"
                    onClick={() => setAgencyPopupOpen(true)}
                    className={`flex items-center gap-2 h-11 px-3.5 rounded-xl border text-left shrink-0 whitespace-nowrap transition-all duration-200 active:scale-[0.97] ${selectedAgency ? "border-[#190276]/25 bg-[#190276]/[0.03] hover:bg-[#190276]/[0.07] hover:border-[#190276]/40" : "border-gray-200/80 bg-white hover:border-gray-300 hover:bg-gray-50/80"}`}
                    aria-label="Choisir mon agence ou showroom"
                    data-testid="header-agency"
                  >
                    <MapPin
                      className={`w-4 h-4 shrink-0 ${selectedAgency ? "text-[#190276]" : "text-gray-400"}`}
                      strokeWidth={1.8}
                    />
                    <span
                      className={`font-medium text-sm ${selectedAgency ? "text-[#190276]" : "text-gray-700"}`}
                    >
                      {selectedAgency
                        ? agencyLabel(selectedAgency)
                        : "Mon lieu EPP"}
                    </span>
                    {selectedAgency && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#190276] shrink-0" />
                    )}
                  </button>
                  {/* Cloche notifications */}
                  {isAuthenticated && (
                    <Link
                      to="/compte/notifications"
                      className="relative flex items-center justify-center w-11 h-11 rounded-xl bg-gray-100/80 text-gray-600 hover:bg-[#190276]/[0.07] hover:text-[#190276] active:scale-[0.97] transition-all duration-200 shrink-0"
                      aria-label="Notifications"
                      data-testid="header-notifications"
                    >
                      <Bell className="w-5 h-5" strokeWidth={1.8} />
                      {notifCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1 shadow-sm border-2 border-white">
                          {notifCount > 99 ? "99+" : notifCount}
                        </span>
                      )}
                    </Link>
                  )}
                  {/* Bouton secondaire — compte */}
                  {isAuthenticated ? (
                    <Link
                      to="/compte"
                      className="flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200/70 hover:bg-emerald-100 hover:text-emerald-700 hover:ring-emerald-300/80 active:scale-[0.97] transition-all duration-200 shrink-0"
                      aria-label="Mon compte — connecté"
                      data-testid="nav-compte"
                      title="Compte connecté"
                    >
                      <UserRound className="w-5 h-5" strokeWidth={2} />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConnexionOpen(true)}
                      className="flex items-center justify-center w-11 h-11 rounded-xl bg-gray-100/80 text-gray-700 hover:bg-gray-200/80 hover:text-[#190276] active:scale-[0.97] transition-all duration-200 shrink-0"
                      aria-label="Se connecter"
                      data-testid="header-connexion"
                    >
                      <UserRound className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                  )}
                  {/* Bouton primaire — panier + dropdown */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={toggleCart}
                      data-cart-toggle
                      className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#190276] text-white hover:bg-[#130160] active:scale-[0.97] transition-all duration-200 shrink-0 relative shadow-sm hover:shadow-md"
                      aria-label="Panier"
                      data-testid="header-cart"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      {cartCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 shadow-sm border-2 border-white">
                          {cartCount > 99 ? "99+" : cartCount}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* ========== Mobile ========== */}
              <div className="lg:hidden min-w-0 overflow-x-visible">
                <div className="flex min-w-0 flex-row items-center gap-1.5 overflow-visible py-2 pr-2 sm:gap-2 sm:py-2 sm:pr-3">
                  <button
                    type="button"
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-700 transition-colors duration-200 hover:bg-gray-50 hover:text-[#190276] sm:h-10 sm:w-10 z-[60]"
                    aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                    aria-expanded={menuOpen}
                    data-testid="mobile-menu-toggle"
                  >
                    {menuOpen ? (
                      <X className="w-6 h-6 stroke-[2]" />
                    ) : (
                      <Menu className="w-6 h-6 stroke-[2]" />
                    )}
                  </button>
                  <Link
                    to="/"
                    className="mr-1 flex max-w-[min(40vw,9.75rem)] shrink-0 items-center self-center sm:mr-2 sm:max-w-[12.5rem]"
                    data-testid="logo-link"
                  >
                    <img
                      src={EPP_LOGO}
                      alt="EPP - Euro Plomberie Piscines"
                      className="h-16 w-auto max-w-full object-contain object-left sm:h-[4.75rem]"
                    />
                  </Link>
                  <div className="min-w-0 flex-1" />
                  <button
                    type="button"
                    onClick={() => setAgencyPopupOpen(true)}
                    className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors duration-200 sm:h-10 sm:w-10 ${selectedAgency ? "text-[#190276] bg-[#190276]/[0.06]" : "text-gray-500 hover:bg-gray-100 hover:text-[#190276]"}`}
                    aria-label="Choisir mon agence ou showroom"
                    data-testid="header-agency-mobile"
                  >
                    <MapPin className="w-5 h-5" strokeWidth={1.8} />
                    {selectedAgency && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#190276] rounded-full border-2 border-white" />
                    )}
                  </button>
                  {isAuthenticated && (
                    <Link
                      to="/compte/notifications"
                      className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100/80 text-gray-500 hover:bg-[#190276]/[0.07] hover:text-[#190276] transition-colors duration-200 sm:h-10 sm:w-10"
                      aria-label="Notifications"
                      data-testid="header-notifications-mobile"
                    >
                      <Bell className="w-5 h-5" strokeWidth={1.8} />
                      {notifCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-bold px-0.5 shadow-sm border-[1.5px] border-white">
                          {notifCount > 99 ? "99+" : notifCount}
                        </span>
                      )}
                    </Link>
                  )}
                  {isAuthenticated ? (
                    <Link
                      to="/compte"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200/70 transition-colors duration-200 hover:bg-emerald-100 hover:text-emerald-700 sm:h-10 sm:w-10"
                      aria-label="Mon compte — connecté"
                      data-testid="nav-compte"
                      title="Compte connecté"
                    >
                      <UserRound className="w-5 h-5" strokeWidth={2} />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConnexionOpen(true)}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-700 transition-colors duration-200 hover:bg-gray-200 hover:text-[#190276] sm:h-10 sm:w-10"
                      aria-label="Se connecter"
                      data-testid="header-connexion"
                    >
                      <UserRound className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={toggleCart}
                    data-cart-toggle
                    className="relative flex h-9 w-9 shrink-0 -translate-x-1 items-center justify-center rounded-xl bg-[#190276] text-white transition-colors duration-200 hover:bg-[#130160] sm:h-10 sm:w-10 sm:translate-x-0"
                    aria-label="Panier"
                    data-testid="header-cart"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-0.5 shadow-sm border-2 border-white">
                        {cartCount > 99 ? "99+" : cartCount}
                      </span>
                    )}
                  </button>
                </div>
                <form
                  onSubmit={handleSearchSubmit}
                  className="relative w-full pt-1.5 pb-3"
                  role="search"
                  aria-label="Rechercher"
                >
                  <div className="flex h-11 w-full items-center overflow-hidden rounded-xl border border-gray-200 bg-white transition-all duration-200 focus-within:border-[#190276] focus-within:ring-1 focus-within:ring-[#190276]/20">
                    <input
                      type="search"
                      name="search"
                      placeholder="Rechercher un produit..."
                      className="flex-1 min-w-0 h-full px-4 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none bg-transparent"
                      aria-label="Recherche catalogue"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() =>
                        setTimeout(() => setSearchFocused(false), 200)
                      }
                      autoComplete="off"
                    />
                    {searching && (
                      <span className="w-4 h-4 border-2 border-[#190276] border-t-transparent rounded-full animate-spin mr-2 shrink-0" />
                    )}
                    <button
                      type="submit"
                      className="flex items-center justify-center w-11 h-full bg-[#190276] text-white hover:bg-[#130160] transition-colors duration-200 shrink-0"
                      aria-label="Rechercher"
                    >
                      <Search className="w-5 h-5" />
                    </button>
                  </div>
                  {searchFocused && searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-2xl z-50 overflow-hidden max-h-[350px] overflow-y-auto">
                      {searchResults.map((p) => {
                        const img =
                          (p.images || []).find(
                            (u) =>
                              typeof u === "string" && u.startsWith("http"),
                          ) || p.image;
                        const price = p.price != null ? Number(p.price) : null;
                        return (
                          <Link
                            key={p.id || p._id}
                            to={`/produit/${p.slug}`}
                            className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                            onClick={() => {
                              setSearchQuery("");
                              setSearchResults([]);
                              setSearchFocused(false);
                            }}
                          >
                            <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-100 shrink-0 overflow-hidden flex items-center justify-center">
                              {img ? (
                                <img
                                  src={img}
                                  alt=""
                                  className="w-full h-full object-contain p-0.5"
                                  loading="lazy"
                                />
                              ) : (
                                <Search className="w-3 h-3 text-gray-300" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {p.name}
                              </p>
                              {price != null && (
                                <span className="text-xs font-bold text-[#190276]">
                                  {price.toFixed(2)} €
                                </span>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                      <Link
                        to={`/catalogue?search=${encodeURIComponent(searchQuery)}`}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold text-[#190276] hover:bg-[#190276]/5"
                        onClick={() => {
                          setSearchQuery("");
                          setSearchResults([]);
                          setSearchFocused(false);
                        }}
                      >
                        Tous les résultats <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* ========== NIVEAU C — Navigation secondaire ========== */}
          <div className="hidden lg:block border-t border-gray-200/70 bg-gradient-to-b from-white via-white to-gray-50/90 backdrop-blur-sm shadow-[0_6px_24px_-12px_rgba(25,2,118,0.12)]">
            <div className="w-full px-4 md:px-6 xl:px-8">
              <nav
                className="flex flex-wrap items-center justify-center xl:justify-start gap-x-0.5 gap-y-1 py-2 min-h-[44px]"
                aria-label="Navigation secondaire"
              >
                {secondaryNavItems.map((item) => {
                  const linkBase =
                    "inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-xl text-gray-700 hover:text-[#190276] hover:bg-[#190276]/[0.07] active:scale-[0.98] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#190276]/30 focus-visible:ring-offset-1";
                  if (item.isProduits) {
                    return (
                      <button
                        key="produits"
                        type="button"
                        onClick={openDrawerProducts}
                        className={`group ${linkBase}`}
                        data-testid="nav-produits"
                      >
                        {item.label}
                        <ChevronDown
                          className="w-4 h-4 shrink-0 text-gray-500 transition-colors group-hover:text-[#190276]"
                          strokeWidth={2.25}
                          aria-hidden
                        />
                      </button>
                    );
                  }
                  if (item.isBrandBlue) {
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMenuOpen(false)}
                        className="ml-0.5 inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-b from-[#190276] to-[#130160] shadow-sm shadow-[#190276]/25 ring-1 ring-[#190276]/45 hover:from-[#24108f] hover:to-[#190276] hover:shadow-md hover:shadow-[#190276]/30 active:scale-[0.98] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#190276]/45 focus-visible:ring-offset-1"
                        data-testid="nav-evenements"
                      >
                        {item.label}
                      </Link>
                    );
                  }
                  if (item.isHighlight) {
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMenuOpen(false)}
                        className="ml-0.5 inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold text-gray-900 bg-gradient-to-b from-[#fcd34d] to-[#F2B531] shadow-sm shadow-amber-900/10 ring-1 ring-amber-400/40 hover:from-[#fde68a] hover:to-[#f5bd2e] hover:shadow-md hover:shadow-amber-900/15 active:scale-[0.98] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 focus-visible:ring-offset-1"
                        data-testid="nav-showroom"
                      >
                        {item.label}
                      </Link>
                    );
                  }
                  if (item.isCta) {
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMenuOpen(false)}
                        className="ml-0.5 inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-b from-[#f7040a] to-[#c90308] shadow-sm shadow-red-950/20 ring-1 ring-red-500/35 hover:from-[#ff1a1f] hover:to-[#f7040a] hover:shadow-md active:scale-[0.98] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/45 focus-visible:ring-offset-1"
                        data-testid="nav-promotions"
                      >
                        {item.label}
                      </Link>
                    );
                  }
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMenuOpen(false)}
                      className={linkBase}
                      data-testid={`nav-${item.label.toLowerCase().replaceAll(" ", "-")}`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Cart dropdown — shared desktop + mobile */}
      <AnimatePresence>
        {cartOpen && (
          <motion.div
            data-cart-dropdown
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed right-2 sm:right-4 xl:right-[max(1rem,calc((100vw-1580px)/2+1rem))] 2xl:right-[max(1rem,calc((100vw-1760px)/2+1rem))] w-[calc(100vw-16px)] sm:w-[380px] bg-white rounded-2xl border border-gray-200 shadow-2xl z-[60] overflow-hidden"
            style={{ top: logoHeaderHeight + 4 }}
          >
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-sm text-gray-900">
                Mon panier{" "}
                <span className="text-gray-400 font-medium">({cartCount})</span>
              </h3>
              <button
                type="button"
                onClick={() => setCartOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-[50vh] sm:max-h-[320px] overflow-y-auto">
              {cartLoading ? (
                <div className="flex items-center justify-center py-10">
                  <span className="w-5 h-5 border-2 border-[#190276] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : cartItems.length === 0 ? (
                <div className="py-10 text-center">
                  <ShoppingCart className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Votre panier est vide</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {cartItems.map((item) => {
                    const img =
                      item.image ||
                      (item.images || []).find(
                        (u) => typeof u === "string" && u.startsWith("http"),
                      );
                    const qty = Number(item.quantity) || 1;
                    const lineTtc = orderAccountLineTtc(item);
                    const lineHt = orderAccountLineHt(item);
                    const strikeUnitHt = isApprovedPro ? strikePublicHtFromCartItem(item) : null;
                    const strikeLineHt =
                      strikeUnitHt != null ? Math.round(strikeUnitHt * qty * 100) / 100 : null;
                    const showStrike =
                      isApprovedPro &&
                      strikeLineHt != null &&
                      lineHt > 0 &&
                      strikeLineHt > lineHt + 0.01;
                    return (
                      <li
                        key={item.product_id || item.id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded-lg bg-gray-50 border border-gray-100 shrink-0 overflow-hidden flex items-center justify-center">
                          {img ? (
                            <img
                              src={img}
                              alt=""
                              className="w-full h-full object-contain p-1"
                              loading="lazy"
                            />
                          ) : (
                            <Package className="w-5 h-5 text-gray-200" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {item.product_name || item.name}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                            <span className="text-xs text-gray-400">
                              Qté: {item.quantity}
                            </span>
                            {item.unit_price != null && (
                              <>
                                {showStrike && (
                                  <span className="text-[11px] text-gray-400 line-through tabular-nums">
                                    {strikeLineHt.toFixed(2)} € HT
                                  </span>
                                )}
                                <span className="text-xs font-bold tabular-nums text-[#190276]">
                                  {isApprovedPro
                                    ? `${lineHt.toFixed(2)} € HT`
                                    : `${lineTtc.toFixed(2)} € TTC`}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            removeCartItem(item.product_id || item.id)
                          }
                          disabled={cartUpdating}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                          aria-label="Retirer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {cartItems.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{isApprovedPro ? "Sous-total HT" : "Sous-total"}</span>
                  <span className="font-bold text-gray-900 tabular-nums">
                    {isApprovedPro
                      ? `${cartItems.reduce((s, i) => s + orderAccountLineHt(i), 0).toFixed(2)} € HT`
                      : `${cartItems.reduce((s, i) => s + orderAccountLineTtc(i), 0).toFixed(2)} € TTC`}
                  </span>
                </div>
                <Link
                  to="/panier"
                  onClick={() => setCartOpen(false)}
                  className="flex items-center justify-center w-full h-11 rounded-xl bg-[#190276] text-white font-semibold text-sm hover:bg-[#130160] transition-colors duration-200"
                >
                  Voir mon panier
                </Link>
                {!isAuthenticated && (
                  <p className="text-[11px] text-center text-gray-400">
                    Connectez-vous au moment de la commande
                  </p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popup Connexion */}
      <Dialog open={connexionOpen} onOpenChange={setConnexionOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-[400px] mx-auto p-0 gap-0 overflow-hidden rounded-2xl border-gray-200 shadow-xl"
          aria-describedby={undefined}
        >
          <div className="h-1.5 flex" aria-hidden>
            <div className="flex-1 bg-[#190276]" />
            <div className="flex-1 bg-[#f5f5f5]" />
            <div className="flex-1 bg-[#f7040a]" />
          </div>
          <div className="px-6 pt-6 pb-6">
            <DialogHeader className="text-center pb-5">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-[#190276]/10 flex items-center justify-center mb-4">
                <UserRound
                  className="w-8 h-8 text-[#190276]"
                  strokeWidth={1.5}
                />
              </div>
              <DialogTitle className="text-xl font-bold text-gray-900">
                Connexion à votre compte
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                Accédez à vos commandes, devis et listes de favoris.
              </p>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <Button
                className="w-full h-12 rounded-xl bg-[#f7040a] hover:bg-[#d40409] text-white font-semibold text-base shadow-sm"
                onClick={() => {
                  setConnexionOpen(false);
                  navigate("/connexion");
                }}
              >
                <UserRound className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Se connecter
              </Button>
              <Button
                variant="outline"
                className="w-full h-12 rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-[#190276]/30 font-medium"
                onClick={() => {
                  setConnexionOpen(false);
                  navigate("/inscription");
                }}
              >
                Créer un compte
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Popup choix agence — feuille basse mobile (hauteur viewport + scroll interne) */}
      <Dialog open={agencyPopupOpen} onOpenChange={setAgencyPopupOpen}>
        <DialogContent
          className="!fixed !left-0 !right-0 !top-auto !bottom-0 !z-50 !flex !h-[min(88dvh,720px)] !max-h-[88dvh] !w-full !max-w-none !translate-x-0 !translate-y-0 !flex-col !gap-0 !overflow-hidden !rounded-t-[1.35rem] !rounded-b-none !border-0 !border-t !border-gray-200/60 !bg-white !p-0 !shadow-[0_-8px_40px_rgba(15,10,36,0.18)] !duration-300 sm:!left-[50%] sm:!right-auto sm:!top-[50%] sm:!bottom-auto sm:!h-auto sm:!max-h-[min(82vh,560px)] sm:!w-[calc(100vw-1.5rem)] sm:!max-w-md sm:!-translate-x-1/2 sm:!-translate-y-1/2 sm:!rounded-3xl sm:!border sm:!border-gray-200/80 sm:!shadow-[0_24px_64px_-12px_rgba(25,2,118,0.22)] [&>button]:hidden"
          aria-describedby="agency-dialog-desc"
        >
          <button
            type="button"
            onClick={() => setAgencyPopupOpen(false)}
            className="absolute right-2.5 top-2.5 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-black/20 text-white backdrop-blur-sm transition hover:bg-black/30 sm:right-3 sm:top-3"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>

          <div className="flex min-h-0 max-h-full flex-1 flex-col max-sm:pb-[env(safe-area-inset-bottom,0px)]">
            {/* Poignée + en-tête compact */}
            <div className="shrink-0 rounded-t-[1.35rem] bg-gradient-to-br from-[#190276] via-[#2a0d9e] to-[#130160] px-4 pb-4 pt-2 text-white sm:rounded-t-3xl sm:px-5 sm:pb-4 sm:pt-4">
              <div className="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-white/35 sm:hidden" aria-hidden />
              <div className="flex items-start gap-3 pr-10 sm:pr-12">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
                  <MapPin className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                  <DialogHeader className="space-y-0 text-left">
                    <DialogTitle className="font-heading text-lg font-bold leading-tight tracking-tight text-white sm:text-xl">
                      Agence de retrait
                    </DialogTitle>
                  </DialogHeader>
                  <p id="agency-dialog-desc" className="mt-1 text-xs leading-snug text-white/80 sm:text-sm sm:leading-relaxed">
                    Click &amp; Collect et stocks selon l&apos;agence choisie.
                  </p>
                </div>
              </div>
            </div>

            {/* Liste : seule zone qui défile — ne dépasse jamais l’écran */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:max-h-[min(52vh,400px)] sm:px-4 sm:py-4">
              <p className="mb-2 px-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                Choisir une agence
              </p>
              <ul className="space-y-1.5 sm:space-y-2" aria-label="Agences avec retrait commande">
                {pickupAgencies.length === 0 && (
                  <li className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-5 text-center text-sm text-gray-600">
                    Aucune agence de retrait pour le moment.
                  </li>
                )}
                {pickupAgencies.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => selectAgency(a)}
                      className={`flex min-h-[48px] w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition sm:min-h-[52px] sm:gap-3 sm:rounded-2xl sm:px-3.5 sm:py-3 ${selectedId === a.id ? "border-[#190276] bg-[#190276]/[0.07] ring-1 ring-[#190276]/12" : "border-gray-100 bg-gray-50/90 hover:border-gray-200 hover:bg-white"}`}
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:h-9 sm:w-9 sm:rounded-xl ${selectedId === a.id ? "bg-[#190276]/15 text-[#190276]" : "bg-white text-gray-400 ring-1 ring-gray-100"}`}
                      >
                        <MapPin className="h-4 w-4 sm:h-[18px] sm:w-[18px]" strokeWidth={2} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <span
                          className={`block text-sm font-semibold leading-snug sm:text-[15px] ${selectedId === a.id ? "text-[#190276]" : "text-gray-900"}`}
                        >
                          {a.name}
                        </span>
                        {a.city ? (
                          <span className="block text-xs text-gray-500">{a.city}</span>
                        ) : null}
                      </div>
                      {selectedId === a.id ? (
                        <span className="shrink-0 rounded-full bg-[#190276] px-2 py-0.5 text-[10px] font-bold text-white sm:px-2.5 sm:text-[11px]">
                          Actif
                        </span>
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 sm:h-5 sm:w-5" strokeWidth={2} aria-hidden />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pied fixe : showroom + safe area iOS */}
            {showroomAgencies.length > 0 && (
              <div className="shrink-0 border-t border-gray-100 bg-slate-50/95 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-4 sm:pb-4">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">
                  <Store className="h-3.5 w-3.5 text-gray-400" strokeWidth={2} />
                  Showroom — pas de retrait web
                </div>
                <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-gray-600 sm:text-xs">
                  Visite et inspiration uniquement.
                </p>
                <Link
                  to="/showroom"
                  onClick={() => setAgencyPopupOpen(false)}
                  className="mt-2 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-white px-3 text-sm font-bold text-[#190276] ring-1 ring-gray-200 transition hover:bg-gray-50"
                >
                  Page Showroom
                  <ChevronRight className="h-4 w-4 shrink-0 opacity-70" />
                </Link>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation changement agence */}
      <Dialog
        open={!!pendingAgency}
        onOpenChange={(open) => {
          if (!open) setPendingAgency(null);
        }}
      >
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-sm rounded-2xl border-0 shadow-xl p-0 overflow-hidden gap-0"
          aria-describedby={undefined}
        >
          <div className="bg-amber-500 text-white px-5 pt-5 pb-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-white">
                Changer d'agence ?
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-700">
              Vous êtes actuellement rattaché à{" "}
              <strong>{selectedAgency?.name}</strong>.
            </p>
            <p className="text-sm text-gray-600">
              En changeant pour <strong>{pendingAgency?.name}</strong>, les
              disponibilités produits, les stocks et votre point de retrait
              Click & Collect seront mis à jour.
            </p>
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setPendingAgency(null)}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => confirmSelectAgency(pendingAgency)}
                className="flex-1 px-4 py-2.5 bg-[#190276] text-white rounded-xl text-sm font-medium hover:bg-[#130160] transition-colors"
              >
                Confirmer
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Overlay — clic pour fermer (plein écran) ; le blur est dans la bande seulement (voir plus bas) */}
      <AnimatePresence>
        {menuOpen && (
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 cursor-default border-0 p-0 w-full"
            style={{ top: logoHeaderHeight }}
            onClick={() => setMenuOpen(false)}
            aria-label="Fermer le menu"
          />
        )}
      </AnimatePresence>

      {/* Desktop : drawer + blur dans la bande shell, animation descente, largeur alignée logo */}
      <AnimatePresence>
        {menuOpen && (
          <div
            className="fixed left-0 right-0 z-50 hidden lg:flex justify-center"
            style={{
              top: logoHeaderHeight,
              height: `calc(100vh - ${logoHeaderHeight}px)`,
            }}
          >
            <div
              ref={menuContainerRef}
              className={`flex h-full w-full ${SITE_SHELL_MAX} mx-auto flex-row overflow-hidden`}
            >
              <motion.div
                initial={{ y: "-100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-100%" }}
                transition={{ type: "tween", duration: 0.3 }}
                style={{
                  width: catalogHoveredKey ? "100%" : `${drawerMainWidth}px`,
                }}
                className="h-full bg-white overflow-hidden shadow-2xl border-t border-gray-200 transition-[width] duration-300 shrink-0"
                data-testid="header-drawer"
              >
                <div className="h-full overflow-hidden flex flex-col">
                  {/* ========== Desktop : flux photo 1 → 2 → 3 ; colonne gauche = largeur logo pour rester alignée ========== */}
                  <div
                    className={`hidden lg:grid h-full overflow-hidden transition-[grid-template-columns] duration-300 ${!catalogHoveredKey ? "grid-cols-[1fr]" : ""}`}
                    style={
                      catalogHoveredKey
                        ? {
                            gridTemplateColumns: `${drawerMainWidth}px 1fr 320px`,
                          }
                        : undefined
                    }
                  >
                    {/* Colonne gauche : vue principale (photo 1) ou liste catégories (photo 2) ; au clic sur une catégorie la droite se déploie (photo 3) */}
                    <aside
                      className={`h-full min-h-0 scrollbar-hide flex flex-col bg-gray-50/60 py-1 ${mobileDrawerView === "categories" ? "overflow-hidden" : "overflow-y-auto"}`}
                    >
                      {mobileDrawerView === "main" ? (
                        <>
                          <div className="px-3 py-2">
                            <div className="flex items-center justify-between gap-2 rounded-xl bg-[#190276] px-3 py-2.5 text-white shadow-md ring-1 ring-[#190276]/30">
                              <span className="flex min-w-0 items-center gap-2 truncate text-sm font-medium">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
                                  <MapPin className="h-4 w-4" strokeWidth={1.8} aria-hidden />
                                </span>
                                <span className="truncate">
                                  {selectedAgency
                                    ? agencyLabel(selectedAgency)
                                    : "Aucun lieu choisi"}
                                </span>
                              </span>
                              <button
                                type="button"
                                onClick={() => setAgencyPopupOpen(true)}
                                className="shrink-0 rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wide text-white/90 transition hover:bg-white/10 hover:text-white"
                              >
                                Changer
                              </button>
                            </div>
                          </div>
                          <nav className="space-y-1 px-2 py-1" aria-label="Navigation principale">
                            {drawerPrimaryItems.main.map((item) =>
                              item.hasSub ? (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => {
                                    setMobileDrawerView("categories");
                                    setCatalogHoveredKey(null);
                                  }}
                                  className="flex min-h-[48px] w-full items-center justify-between rounded-xl border border-gray-200/80 bg-white/80 px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-800 shadow-sm transition hover:bg-white hover:text-[#190276]"
                                >
                                  {item.label}
                                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                                </button>
                              ) : (
                                <Link
                                  key={item.id}
                                  to={item.path}
                                  onClick={() => setMenuOpen(false)}
                                  className={`flex min-h-[48px] w-full items-center justify-between rounded-xl px-3 py-3 text-left text-xs font-bold uppercase tracking-wide transition ${
                                    item.isCta
                                      ? "bg-gradient-to-b from-[#f7040a] to-[#c90308] text-white shadow-md ring-1 ring-red-500/20 hover:brightness-105"
                                      : item.isBrandBlue
                                        ? "bg-gradient-to-b from-[#190276] to-[#130160] text-white shadow-md ring-1 ring-[#190276]/35 hover:brightness-105"
                                        : item.isHighlight
                                          ? "bg-gradient-to-b from-[#fcd34d] to-[#F2B531] text-gray-900 shadow-md ring-1 ring-amber-400/35 hover:brightness-105"
                                          : "border border-transparent bg-white/60 text-gray-800 hover:bg-white hover:text-[#190276] hover:shadow-sm"
                                  }`}
                                >
                                  {item.label}
                                  <ChevronRight
                                    className={`h-4 w-4 shrink-0 ${
                                      item.isCta || item.isBrandBlue
                                        ? "text-white/90"
                                        : item.isHighlight
                                          ? "text-gray-800/80"
                                          : "text-gray-400"
                                    }`}
                                    aria-hidden
                                  />
                                </Link>
                              ),
                            )}
                          </nav>
                          {isAuthenticated && (
                            <div className="mx-3 mt-5 rounded-2xl border border-gray-200/80 bg-white/90 p-1 shadow-sm">
                              <p className="px-3 pt-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#190276]/55">
                                Mon compte
                              </p>
                              <ul className="pb-2">
                                {drawerPrimaryItems.account.map((item) => (
                                  <li key={item.id}>
                                    <Link
                                      to={item.path}
                                      onClick={() => setMenuOpen(false)}
                                      className="mx-1 flex min-h-[44px] items-center rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-[#190276]/[0.06] hover:text-[#190276]"
                                    >
                                      {item.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                          <button
                            type="button"
                            onClick={() => {
                              setMobileDrawerView("main");
                              setCatalogHoveredKey(null);
                            }}
                            className="flex min-h-[48px] w-full shrink-0 items-center gap-2 border-b border-gray-100 bg-white px-3 py-2.5 text-left text-[#190276] shadow-sm active:bg-gray-50/80"
                            aria-label="Retour au menu"
                          >
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#190276]/[0.08]">
                              <ChevronLeft className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden />
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em]">Menu</span>
                          </button>
                          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide bg-gradient-to-b from-[#f0f2f8] to-[#f7f8fb] px-2 py-3">
                            <p className="mb-2 px-1 text-[9px] font-bold uppercase tracking-[0.26em] text-[#190276]/45">
                              Catalogue
                            </p>
                            <Link
                              to="/catalogue"
                              onClick={() => setMenuOpen(false)}
                              className="mb-3 flex min-h-[52px] w-full items-center justify-between gap-2 rounded-xl bg-[#190276] px-3 py-2.5 text-white shadow-md ring-1 ring-white/10 transition hover:brightness-105 active:scale-[0.99]"
                            >
                              <span className="min-w-0 text-left">
                                <span className="block text-[9px] font-bold uppercase tracking-wider text-white/75">
                                  Accès direct
                                </span>
                                <span className="mt-0.5 block text-sm font-bold leading-tight">
                                  Tout le catalogue
                                </span>
                              </span>
                              <ChevronRight className="h-4 w-4 shrink-0 text-white/90" strokeWidth={2.25} aria-hidden />
                            </Link>
                            <p className="mb-1.5 px-1 text-[9px] font-bold uppercase tracking-[0.2em] text-gray-500">
                              Rayons
                            </p>
                            <ul className="space-y-1.5" aria-label="Familles produits">
                              {catalogueFamilies.map((fam) => {
                                const isActive = catalogHoveredKey === fam.panelKey;
                                return (
                                  <li key={fam.id}>
                                    <button
                                      type="button"
                                      onClick={() => setCatalogHoveredKey(fam.panelKey)}
                                      className={`flex min-h-[48px] w-full items-center gap-2 rounded-xl border px-2 py-2 text-left text-sm font-semibold leading-snug shadow-sm transition active:scale-[0.99] ${
                                        isActive
                                          ? "border-[#190276] bg-white text-[#190276] ring-2 ring-[#190276]/20"
                                          : "border-gray-200/80 bg-white text-gray-900 ring-1 ring-gray-100/80 hover:border-[#190276]/30 hover:text-[#190276]"
                                      }`}
                                    >
                                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#190276]/[0.08] to-[#190276]/[0.02] ring-1 ring-[#190276]/10">
                                        <img
                                          src={fam.image}
                                          alt=""
                                          className="h-5 w-5 object-contain"
                                          loading="lazy"
                                          decoding="async"
                                        />
                                      </span>
                                      <span className="min-w-0 flex-1">{fam.label}</span>
                                      <ChevronRight
                                        className={`h-4 w-4 shrink-0 ${isActive ? "text-[#190276]" : "text-gray-300"}`}
                                        strokeWidth={2}
                                        aria-hidden
                                      />
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      )}
                    </aside>
                    {/* Panneau central + promo : visibles uniquement quand une catégorie est sélectionnée ; sinon blur à droite (pas de phrase, pas de blanc) */}
                    {catalogHoveredKey && (
                      <>
                        <section className="h-full min-h-0 overflow-y-auto scrollbar-hide border-l border-gray-100/80 bg-gradient-to-br from-[#fafbfc] via-white to-white px-6 py-6 lg:px-8 lg:py-7">
                          <MenuPanelLevel2
                            familySlug={catalogHoveredKey}
                            familyLabel={
                              catalogueFamilies.find(
                                (f) => f.panelKey === catalogHoveredKey,
                              )?.label
                            }
                            children={panelChildren}
                            onClose={() => setMenuOpen(false)}
                          />
                        </section>
                        <aside className="flex h-full min-h-0 flex-col overflow-y-auto scrollbar-hide border-l border-gray-200/60 bg-gradient-to-b from-[#f0f2f8] to-[#f7f8fb] p-4 lg:p-5">
                          <div className="flex min-h-0 flex-1 flex-col">
                            {catalogueDrawerPromo?.image ? (
                              <div className="relative mb-4 w-full max-h-[240px] overflow-hidden rounded-2xl bg-gray-200 shadow-md ring-1 ring-gray-200/80">
                                <div
                                  className="flex h-1.5 w-full shrink-0"
                                  aria-hidden
                                >
                                  <span className="flex-1 bg-[#002395]" />
                                  <span className="flex-1 bg-[#f5f5f5]" />
                                  <span className="flex-1 bg-[#ED2939]" />
                                </div>
                                <div className="aspect-[4/3] w-full max-h-[220px]">
                                  <img
                                    src={catalogueDrawerPromo.image}
                                    alt=""
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                              </div>
                            ) : null}
                            {catalogueDrawerPromo?.title ? (
                              <div className="mb-1">
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#c9a017]">
                                  Inspiration
                                </p>
                                <p className="mt-1.5 text-base font-bold leading-snug text-gray-900">
                                  {catalogueDrawerPromo.title}
                                </p>
                              </div>
                            ) : null}
                            <div className="min-h-3 flex-1" aria-hidden />
                            {catalogueDrawerPromo?.ctaPath ? (
                              <Link
                                to={catalogueDrawerPromo.ctaPath}
                                onClick={() => setMenuOpen(false)}
                                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#190276] px-4 py-3 text-sm font-bold text-white shadow-lg ring-1 ring-white/10 transition hover:brightness-105 active:scale-[0.99]"
                              >
                                {catalogueDrawerPromo.ctaLabel ?? "Je découvre"}
                                <ChevronRight className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                              </Link>
                            ) : null}
                          </div>
                        </aside>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
              {/* Zone blur à droite du menu, dans la bande shell uniquement */}
              <button
                type="button"
                className="flex-1 min-w-0 h-full bg-black/15 backdrop-blur-sm cursor-default border-0"
                onClick={() => setMenuOpen(false)}
                aria-label="Fermer le menu"
              />
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Mobile : menu pleine largeur, pas de blur, chaque vue remplace la précédente */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 lg:hidden"
            style={{ top: logoHeaderHeight }}
          >
            <div className="h-full w-full bg-white flex flex-col overflow-hidden">
              {(mobileDrawerView === "categories" ||
                mobileDrawerView === "panel") && (
                <button
                  type="button"
                  onClick={() =>
                    mobileDrawerView === "panel"
                      ? setMobileDrawerView("categories")
                      : setMobileDrawerView("main")
                  }
                  className="flex min-h-[52px] shrink-0 items-center gap-2 border-b border-gray-100 bg-white px-4 py-3 text-[#190276] shadow-[0_6px_24px_-12px_rgba(25,2,118,0.1)] active:bg-gray-50/80"
                  aria-label="Retour"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#190276]/[0.08]">
                    <ChevronLeft className="h-5 w-5 shrink-0" strokeWidth={2.25} aria-hidden />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-[0.18em]">
                    {mobileDrawerView === "panel" ? "Catalogue" : "Menu"}
                  </span>
                </button>
              )}
              <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                {mobileDrawerView === "main" && (
                  <div className="px-3 pb-6 pt-3">
                    <div className="flex items-center justify-between gap-2 rounded-2xl bg-[#190276] px-3.5 py-3 text-white shadow-md ring-1 ring-[#190276]/40">
                      <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
                          <MapPin className="h-4 w-4" strokeWidth={1.8} aria-hidden />
                        </span>
                        <span className="truncate">
                          {selectedAgency
                            ? agencyLabel(selectedAgency)
                            : "Aucun lieu choisi"}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setAgencyPopupOpen(true)}
                        className="shrink-0 rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wide text-white/95 transition hover:bg-white/10 active:bg-white/15"
                      >
                        Changer
                      </button>
                    </div>
                    <nav className="mt-4 space-y-1.5" aria-label="Navigation principale">
                      {drawerPrimaryItems.main.map((item) =>
                        item.hasSub ? (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => setMobileDrawerView("categories")}
                            className="flex min-h-[52px] w-full items-center justify-between rounded-xl border border-gray-100 bg-gray-50/90 px-4 py-3.5 text-left text-sm font-bold uppercase tracking-wide text-gray-900 shadow-sm transition active:scale-[0.99] active:bg-gray-100"
                          >
                            {item.label}
                            <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden />
                          </button>
                        ) : (
                          <Link
                            key={item.id}
                            to={item.path}
                            onClick={() => setMenuOpen(false)}
                            className={`flex min-h-[52px] w-full items-center justify-between rounded-xl px-4 py-3.5 text-left text-sm font-bold uppercase tracking-wide transition active:scale-[0.99] ${
                              item.isCta
                                ? "bg-gradient-to-b from-[#f7040a] to-[#c90308] text-white shadow-md shadow-red-900/15 ring-1 ring-red-500/25"
                                : item.isBrandBlue
                                  ? "bg-gradient-to-b from-[#190276] to-[#130160] text-white shadow-md shadow-[#190276]/20 ring-1 ring-[#190276]/35"
                                  : item.isHighlight
                                    ? "bg-gradient-to-b from-[#fcd34d] to-[#F2B531] text-gray-900 shadow-md shadow-amber-900/10 ring-1 ring-amber-400/30"
                                    : "border border-transparent bg-white text-gray-900 shadow-sm ring-1 ring-gray-100/90 hover:bg-gray-50/80"
                            }`}
                          >
                            {item.label}
                            <ChevronRight
                              className={`h-5 w-5 shrink-0 ${
                                item.isCta || item.isBrandBlue
                                  ? "text-white/90"
                                  : item.isHighlight
                                    ? "text-gray-800/80"
                                    : "text-gray-400"
                              }`}
                              aria-hidden
                            />
                          </Link>
                        ),
                      )}
                    </nav>
                    {isAuthenticated && (
                      <div className="mt-6 rounded-2xl border border-gray-100 bg-gradient-to-b from-gray-50/90 to-white p-1 shadow-[0_8px_30px_-12px_rgba(25,2,118,0.12)]">
                        <p className="px-3.5 pt-3.5 pb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#190276]/55">
                          Mon compte
                        </p>
                        <ul className="space-y-0.5 px-1 pb-2">
                          {drawerPrimaryItems.account.map((item) => (
                            <li key={item.id}>
                              <Link
                                to={item.path}
                                onClick={() => setMenuOpen(false)}
                                className="flex min-h-[48px] items-center rounded-xl px-3 py-2.5 text-[15px] font-medium text-gray-700 transition active:bg-gray-100/80 hover:bg-[#190276]/[0.05] hover:text-[#190276]"
                              >
                                {item.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {mobileDrawerView === "categories" && (
                  <div className="bg-gradient-to-b from-[#f0f2f8] to-[#f7f8fb] px-3 pb-8 pt-4">
                    <p className="mb-3 px-1 text-[10px] font-bold uppercase tracking-[0.28em] text-[#190276]/45">
                      Catalogue
                    </p>
                    <Link
                      to="/catalogue"
                      onClick={() => setMenuOpen(false)}
                      className="mb-5 flex min-h-[56px] w-full items-center justify-between gap-3 rounded-2xl bg-[#190276] px-4 py-3.5 text-white shadow-lg shadow-[#190276]/25 ring-1 ring-white/10 transition active:scale-[0.99] hover:brightness-105"
                    >
                      <span className="text-left">
                        <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-white/70">
                          Accès direct
                        </span>
                        <span className="mt-0.5 block text-base font-bold leading-tight">
                          Voir tout le catalogue
                        </span>
                      </span>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15">
                        <ChevronRight className="h-5 w-5 text-white" strokeWidth={2.25} aria-hidden />
                      </span>
                    </Link>
                    <p className="mb-2.5 px-1 text-[10px] font-bold uppercase tracking-[0.22em] text-gray-500">
                      Rayons
                    </p>
                    <ul className="space-y-2" aria-label="Familles produits">
                      {catalogueFamilies.map((fam) => (
                        <li key={fam.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setMobilePanelKey(fam.panelKey);
                              setMobileDrawerView("panel");
                            }}
                            className="flex min-h-[58px] w-full items-center gap-3 rounded-2xl border border-gray-200/80 bg-white px-3 py-2.5 text-left shadow-sm shadow-gray-200/40 ring-1 ring-gray-100/80 transition active:scale-[0.99] active:bg-gray-50/90"
                          >
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#190276]/[0.07] to-[#190276]/[0.02] ring-1 ring-[#190276]/10">
                              <img
                                src={fam.image}
                                alt=""
                                className="h-7 w-7 object-contain"
                                loading="lazy"
                                decoding="async"
                              />
                            </span>
                            <span className="min-w-0 flex-1 text-[15px] font-semibold leading-snug text-gray-900">
                              {fam.label}
                            </span>
                            <ChevronRight
                              className="h-5 w-5 shrink-0 text-gray-300"
                              strokeWidth={2}
                              aria-hidden
                            />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {mobileDrawerView === "panel" && mobilePanelKey && (
                  <section className="min-h-full bg-gradient-to-b from-[#f0f2f8] to-[#f7f8fb] pb-10">
                    <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-gray-200/80 bg-white/90 px-4 py-3.5 shadow-sm backdrop-blur-md">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#190276]/50">
                          Sous-catégories
                        </p>
                        <span className="mt-0.5 block truncate text-base font-bold text-gray-900">
                          {catalogueFamilies.find(
                            (f) => f.panelKey === mobilePanelKey,
                          )?.label || mobilePanelKey}
                        </span>
                      </div>
                      <Link
                        to={`/catalogue/${mobilePanelKey}`}
                        onClick={() => setMenuOpen(false)}
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#190276] px-3.5 py-2 text-xs font-bold text-white shadow-md shadow-[#190276]/20 transition active:scale-[0.98] hover:brightness-105"
                      >
                        Tout voir
                        <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      </Link>
                    </div>
                    <div className="px-3 pt-4">
                      {panelChildren?.length > 0 ? (
                        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {panelChildren.map((c) => (
                            <li key={c.id}>
                              <Link
                                to={`/catalogue/${mobilePanelKey}/${c.slug}`}
                                onClick={() => setMenuOpen(false)}
                                className="flex min-h-[48px] items-center rounded-xl border border-gray-200/80 bg-white px-3.5 py-2.5 text-sm font-medium leading-snug text-gray-800 shadow-sm ring-1 ring-gray-100/60 transition hover:border-[#190276]/25 hover:text-[#190276] active:bg-gray-50"
                              >
                                {c.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <Link
                          to={`/catalogue/${mobilePanelKey}`}
                          onClick={() => setMenuOpen(false)}
                          className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-[#190276] px-5 py-3 text-sm font-bold text-white shadow-md"
                        >
                          Voir la catégorie
                        </Link>
                      )}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Suggestion agence la plus proche (hors showroom) — mobile first */}
      {!selectedId && suggestedStore && (
        <div
          className="fixed left-0 right-0 z-[60] animate-[fadeIn_0.3s_ease-out] px-2 sm:px-0"
          style={{
            top:
              (demoOn ? DEMO_BANNER_HEIGHT_PX : 0) +
              (headerHeight || headerRef.current?.offsetHeight || 140),
          }}
        >
          <section
            className={`${SITE_SHELL_MAX} mx-auto flex w-full flex-col gap-3 border border-emerald-200/80 bg-gradient-to-br from-emerald-50 via-white to-teal-50/40 px-4 py-3 shadow-md shadow-emerald-900/5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:rounded-b-2xl sm:border-t-0 sm:border-x sm:border-b sm:px-5`}
            aria-label="Suggestion d'agence"
          >
            <p className="flex min-w-0 items-start gap-2.5 text-sm leading-snug text-emerald-950">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
                <MapPin className="h-4 w-4" strokeWidth={2} />
              </span>
              <span className="min-w-0 pt-0.5">
                <span className="block font-bold text-emerald-900 sm:inline sm:mr-1">Agence la plus proche</span>
                <span className="block font-medium text-emerald-800/95 sm:inline">
                  {suggestedStore.name}
                  {suggestedStore.city ? ` — ${suggestedStore.city}` : ""}
                </span>
              </span>
            </p>
            <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <button
                type="button"
                className="min-h-[44px] w-full rounded-xl bg-emerald-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700 active:scale-[0.99] sm:min-h-0 sm:w-auto sm:px-5 sm:py-2.5"
                onClick={() => {
                  setSelectedId(suggestedStore.id);
                  setSuggestedStoreId(null);
                }}
              >
                Choisir cette agence
              </button>
              <button
                type="button"
                className="min-h-[44px] w-full rounded-xl border border-emerald-300/80 bg-white/80 px-4 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-50 sm:min-h-0 sm:w-auto sm:px-4 sm:py-2.5"
                onClick={() => setSuggestedStoreId(null)}
              >
                Fermer
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};
